import { Injectable } from '@angular/core';
import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class FilterService {

    constructor() { }

    /**
     * Updates filter options based on search results from a MatTableDataSource.
     * @param dataSource The MatTableDataSource containing the data.
     * @param searchText The current search text.
     * @param sortFilters An optional function to sort the filter options.
     * @returns An object containing the updated filter options.
     */
    updateFiltersForSearch(dataSource: any, searchText: string, sortFilters?: (options: any) => any) {
        const hasSearch = searchText && searchText.trim().length > 0;

        let options;
        if (hasSearch && dataSource && dataSource.filteredData) {
            // Use filtered data when search is active
            const filteredData = dataSource.filteredData;
            options = this.getFilterOptions(filteredData, {});
        } else {
            // Use all data when no search
            options = this.getFilterOptions(dataSource?.data || [], {});
        }

        // Apply sorting if provided
        if (sortFilters) {
            options = sortFilters(options);
        }

        return options;
    }

    /**
     * Applies filters to the provided data array.
     * @param allData The original data array.
     * @param filters An object containing active filter values (topic, subtopic, sender, form).
     * @param flaggedFilter Optional flag to filter by flagged status.
     * @returns The filtered data array.
     */
    applyFilters(allData: any[], filters: { topic?: string, subtopic?: string, sender?: string, form?: string }, flaggedFilter?: boolean): any[] {
        let filteredData = [...allData];

        if (filters.topic) {
            filteredData = filteredData.filter(form => form.sortTopics === filters.topic);
        }
        if (filters.subtopic) {
            filteredData = filteredData.filter(form => form.subTopics === filters.subtopic);
        }
        if (filters.sender) {
            filteredData = filteredData.filter(form => form.sortSenders === filters.sender);
        }
        if (filters.form) {
            filteredData = filteredData.filter(form => form.sortForms === filters.form);
        }
        if (flaggedFilter) {
            filteredData = filteredData.filter(form => form.is_flagged === true);
        }

        return filteredData;
    }

    /**
     * Updates the available filter options based on current active filters (faceted search).
     * @param allData The original data array.
     * @param filters An object containing active filter values.
     * @returns An object containing arrays of unique filter options for each category.
     */
    getFilterOptions(allData: any[], filters: { topic?: string, subtopic?: string, sender?: string, form?: string }): { topics: string[], subTopics: string[], senders: string[], forms: string[] } {
        const data = allData || [];

        // Topics based on (Subtopic AND Sender AND Form)
        let topicData = data;
        if (filters.subtopic) topicData = topicData.filter(f => f.subTopics === filters.subtopic);
        if (filters.sender) topicData = topicData.filter(f => f.sortSenders === filters.sender);
        if (filters.form) topicData = topicData.filter(f => f.sortForms === filters.form);
        const topics = ["All Topics", ..._.uniq(topicData.map(f => f.sortTopics)).filter(Boolean)];

        // Subtopics based on (Topic AND Sender AND Form)
        let subtopicData = data;
        if (filters.topic) subtopicData = subtopicData.filter(f => f.sortTopics === filters.topic);
        if (filters.sender) subtopicData = subtopicData.filter(f => f.sortSenders === filters.sender);
        if (filters.form) subtopicData = subtopicData.filter(f => f.sortForms === filters.form);
        const subTopics = ["All Sub Topics", ..._.uniq(subtopicData.map(f => f.subTopics)).filter(Boolean)];

        // Senders based on (Topic AND Subtopic AND Form)
        let senderData = data;
        if (filters.topic) senderData = senderData.filter(f => f.sortTopics === filters.topic);
        if (filters.subtopic) senderData = senderData.filter(f => f.subTopics === filters.subtopic);
        if (filters.form) senderData = senderData.filter(f => f.sortForms === filters.form);
        const senders = ["All Senders", ..._.uniq(senderData.map(f => f.sortSenders)).filter(Boolean)];

        // Forms based on (Topic AND Subtopic AND Sender)
        let formListData = data;
        if (filters.topic) formListData = formListData.filter(f => f.sortTopics === filters.topic);
        if (filters.subtopic) formListData = formListData.filter(f => f.subTopics === filters.subtopic);
        if (filters.sender) formListData = formListData.filter(f => f.sortSenders === filters.sender);
        const forms = ["All Forms", ..._.uniq(formListData.map(f => f.sortForms)).filter(Boolean)];

        return { topics, subTopics, senders, forms };
    }

    /**
     * Sorts filter options and ensures the default label is at the beginning.
     * @param options The array of options to sort.
     * @param label The default "All..." label.
     * @returns The sorted array of options.
     */
    sortFilterOptions(options: string[], label: string): string[] {
        let sorted = options.filter(item => item !== label && item !== "" && item !== "A to Z" && item !== "Z to A");
        sorted.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        sorted.unshift(label);
        return sorted;
    }

    /**
     * Checks if any of the filters are active.
     * @param filters The filters object.
     * @returns True if any filter has a value.
     */
    hasActiveFilters(filters: { topic?: string, subtopic?: string, sender?: string, form?: string }): boolean {
        return !!(filters.topic || filters.subtopic || filters.sender || filters.form);
    }
}
